import { Badge, Button, CopyButton, ActionIcon, Group, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core'
import { IconCheck, IconCopy } from '@tabler/icons-react'
import { Callout, DataCard } from '@/components/ui'
import { entraIssuer, entraRedirectUri, type SsoConnection } from '@/api/sso'
import { ssoStatusMeta } from './ssoStatus'

interface Props {
  conexao: SsoConnection
  podeGerenciar: boolean
  temAppAtiva: boolean
  onTrocar: () => void
  onReprovisionar: () => void
  onPausarOuReativar: () => void
  onExcluir: () => void
}

// Código estável do ciclo de sincronização -> o que a pessoa precisa fazer.
const ERRO_SYNC: Record<string, string> = {
  graph_sem_permissao: 'Falta a permissão User.Read.All no app do Entra',
}

const MONO = { fontFamily: 'var(--mantine-font-family-monospace)', wordBreak: 'break-all' as const }

function Par({ rotulo, valor, copiar }: { rotulo: string; valor: string; copiar?: boolean }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{rotulo}</Text>
      <Group gap={4} wrap="nowrap" align="flex-start">
        <Text size="sm" style={MONO}>{valor}</Text>
        {copiar && (
          <CopyButton value={valor}>
            {({ copied, copy }) => (
              <ActionIcon variant="subtle" size="sm" aria-label={`Copiar ${rotulo}`} onClick={copy}>
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            )}
          </CopyButton>
        )}
      </Group>
    </div>
  )
}

export function ProvedorEntraCard({
  conexao, podeGerenciar, temAppAtiva, onTrocar, onReprovisionar, onPausarOuReativar, onExcluir,
}: Props) {
  const meta = ssoStatusMeta(conexao.status)
  const realm = conexao.keycloak_realm ?? ''
  const guid = conexao.config.entra_tenant_id ?? ''
  const erro = conexao.config.last_error
  const sync = conexao.diretorio_sync
  return (
    <DataCard title="Provedor de identidade: Entra ID" extra={<Badge color={meta.color} variant="light">{meta.label}</Badge>}>
      <Stack gap="md">
        {erro && <Callout variant="danger">{erro}</Callout>}
        {sync?.ultimo_erro && (
          <Callout variant="warning" title="Sincronização do diretório">
            {ERRO_SYNC[sync.ultimo_erro] ?? `Falha na última sincronização: ${sync.ultimo_erro}`}
          </Callout>
        )}
        {(!sync || (!sync.ultima_sync_em && !sync.ultimo_erro)) && (
          <Text size="sm" c="dimmed">Diretório ainda não sincronizado</Text>
        )}
        {sync?.ultima_sync_em && (
          <Text size="sm">
            Última sincronização: {new Date(sync.ultima_sync_em).toLocaleString('pt-BR')}
            {sync.usuarios_ativos !== null && ` · ${sync.usuarios_ativos} usuários ativos`}
          </Text>
        )}
        {/* O nome é o que a pessoa vê no login; o realm é o identificador técnico, que
            aparece no issuer e na redirect URI do Entra e não muda. */}
        <div>
          <Text fw={600} size="lg">{conexao.name}</Text>
          <Text size="xs" c="dimmed">realm <span style={MONO}>{realm}</span></Text>
        </div>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Par rotulo="Tenant do Entra" valor={guid} />
          <Par rotulo="Client ID Entra" valor={conexao.config.client_id ?? ''} />
          <Par rotulo="Client secret" valor={conexao.has_secret ? 'gravado' : 'não gravado'} />
          {conexao.updated_at && (
            <Par rotulo="Última alteração" valor={new Date(conexao.updated_at).toLocaleString('pt-BR')} />
          )}
          <Par rotulo="Redirect URI (cadastrar no Entra)" valor={entraRedirectUri(realm)} copiar />
          <Par rotulo="Issuer" valor={entraIssuer(guid)} />
        </SimpleGrid>
        {podeGerenciar && (
          <Group gap="sm" justify="flex-start">
            {/* Pausado: o backend recusa alterar (connection_not_updatable_in_paused). */}
            {conexao.status !== 'paused' && (
              <Button size="xs" variant="default" onClick={onTrocar}>Editar provedor</Button>
            )}
            {/* O backend só reexecuta o provisionamento a partir de draft/error; ativo re-empurra por Trocar. */}
            {(conexao.status === 'draft' || conexao.status === 'error') && (
              <Button size="xs" variant="default" onClick={onReprovisionar}>Reprovisionar</Button>
            )}
            {(conexao.status === 'active' || conexao.status === 'paused') && (
              <Button size="xs" variant="default" onClick={onPausarOuReativar}>
                {conexao.status === 'active' ? 'Pausar' : 'Reativar'}
              </Button>
            )}
            <Tooltip label="Pause ou exclua as aplicações ativas do realm antes" disabled={!temAppAtiva}>
              <span>
                <Button size="xs" color="red" variant="outline" onClick={onExcluir} disabled={temAppAtiva}>
                  Excluir
                </Button>
              </span>
            </Tooltip>
          </Group>
        )}
      </Stack>
    </DataCard>
  )
}
